// form
import { Controller, useFormContext } from 'react-hook-form';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { InputAdornment } from '@mui/material';
// @mui
import { DatePicker, DatePickerProps } from '@mui/x-date-pickers/DatePicker';
import { renderDigitalClockTimeView } from '@mui/x-date-pickers/timeViewRenderers';
import { DateRangePicker, DateRangePickerProps } from '@mui/x-date-pickers-pro/DateRangePicker';
import {
  DateTimeRangePicker,
  DateTimeRangePickerProps,
} from '@mui/x-date-pickers-pro/DateTimeRangePicker';
import { SingleInputDateRangeField } from '@mui/x-date-pickers-pro/SingleInputDateRangeField';
import { endOfMonth, format, isDate, isValid, startOfMonth } from 'date-fns';

interface RHFDatePickerProps extends DatePickerProps<Date> {
  name: string;
  size?: 'medium' | 'small' | undefined;
  readonly?: boolean;
}

export default function RHFDatePicker({
  name,
  size = 'small',
  readonly,
  ...other
}: RHFDatePickerProps) {
  const { control } = useFormContext();
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        const date = new Date(field.value);
        // const dateValue = isDate(date) ? date : new Date();
        const dateValue = isDate(date) && !isNaN(date.getTime()) ? date : null;
        return (
          <DatePicker
            {...field}
            value={dateValue}
            onChange={(newValue) => {
              if (newValue) {
                try {
                  const formattedDate = format(new Date(newValue), 'yyyy-MM-dd');
                  field.onChange(formattedDate);
                } catch (error) {
                  field.onChange(null);
                }
              }
            }}
            slotProps={{
              textField: {
                size: size,
                error: Boolean(error),
                helperText: error ? error.message : undefined,
                InputProps: {
                  readOnly: readonly ?? false,
                },
              },
            }}
            {...other}
          />
        );
      }}
    />
  );
}
interface RHFDateTimeRangePickerProps extends DateTimeRangePickerProps<Date> {
  startNm?: string; // 시작일 필드 이름
  endNm?: string; // 종료일 필드 이름
  emptyInitial?: boolean; // 초기값을 비워두는 옵션: true일 경우 입력란이 비워져서 표시됨
  startLabel?: string; // 시작일 라벨
  endLabel?: string; // 종료일 라벨
  disabled?: boolean; // 비활성화 옵션
}

export function RHFDateTimeRangePicker({
  startNm = 'startDt',
  endNm = 'endDt',
  emptyInitial = false,
  disabled = false,
  startLabel = '시작일',
  endLabel = '종료일',
  ...other
}: RHFDateTimeRangePickerProps) {
  const { setValue, getValues } = useFormContext();
  const parseDate = (date: number | string | undefined): Date | null => {
    if (typeof date === 'number' || typeof date === 'string') {
      const parsedDate = new Date(Number(date));
      return isValid(parsedDate) ? parsedDate : null;
    }
    return null;
  };

  const initialStartDate = getValues(startNm);
  const initialEndDate = getValues(endNm);

  let startDate = initialStartDate ? parseDate(initialStartDate) : null;
  let endDate = initialEndDate ? parseDate(initialEndDate) : null;

  if (emptyInitial) {
    startDate = endDate = null;
  }

  return (
    <DateTimeRangePicker
      value={[startDate, endDate]}
      onChange={(newValue) => {
        if (typeof newValue === 'object' && newValue !== null) {
          const [start, end] = newValue;
          if (start && end) {
            setValue(startNm, format(start, "yyyy-MM-dd'T'HH:mm:ss.SSSX"));
            setValue(endNm, format(end, "yyyy-MM-dd'T'HH:mm:ss.SSSX"));
          }
        }
      }}
      views={['day', 'hours']}
      timeSteps={{ minutes: 20 }}
      viewRenderers={{ hours: renderDigitalClockTimeView }}
      slotProps={{
        textField: ({ position }) => ({
          size: 'small',
          label: position === 'start' ? startLabel : endLabel,
        }),
      }}
      disabled={disabled}
      {...other}
    />
  );
}

// 월별 선택
interface RHFMonthPickerProps extends DatePickerProps<Date> {
  name: string;
  label: string;
  startNm?: string; // 시작 날짜 필드 이름
  endNm?: string; // 종료 날짜 필드 이름
  emptyInitial?: boolean; // 초기값을 비워두는 옵션: true일 경우 입력란이 비워져서 표시됨
}
export function RHFMonthPicker({
  name,
  emptyInitial = false,
  startNm = 'applyStDay',
  endNm = 'applyEdDay',
  ...other
}: RHFMonthPickerProps) {
  const { control, setValue } = useFormContext();
  // 유효한 날짜 값을 설정
  const parseDate = (date: number | string | undefined): Date | null => {
    if (typeof date === 'number' || typeof date === 'string') {
      const parsedDate = new Date(Number(date));
      return isValid(parsedDate) ? parsedDate : null;
    }
    return null;
  };
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        // 필드에 값이 있으면 해당 값을 Date 객체로 변환, emptyInitial이면 날짜 표기 없음
        const dateValue = emptyInitial ? null : parseDate(field.value);
        return (
          <DatePicker
            {...field}
            views={['year', 'month']}
            format="yyyy-MM"
            value={dateValue}
            onChange={(newValue) => {
              if (newValue) {
                const startDate = startOfMonth(newValue);
                const endDate = endOfMonth(newValue);
                setValue(startNm, format(startDate, 'yyyy-MM-dd'));
                setValue(endNm, format(endDate, 'yyyy-MM-dd'));
              }
            }}
            slotProps={{
              textField: {
                sx: { width: '100%' },
                size: 'small',
                error: Boolean(error),
                helperText: error ? error.message : undefined,
              },
            }}
            {...other}
          />
        );
      }}
    />
  );
}

// 기간 선택
interface RHFDateRangePickerProps extends DateRangePickerProps<Date> {
  label: string;
  startNm?: string; // 시작 날짜 필드 이름
  endNm?: string; // 종료 날짜 필드 이름
  size?: 'small' | 'medium';
  emptyInitial?: boolean; // 초기값을 비워두는 옵션: true일 경우 입력란이 비워져서 표시됨
  disabled?: boolean;
}

export function RHFDateRangePicker({
  label,
  startNm = 'applyStDay',
  endNm = 'applyEdDay',
  size = 'small',
  emptyInitial = false,
  disabled = false,
  ...other
}: RHFDateRangePickerProps) {
  const { setValue, getValues } = useFormContext();
  // 유효한 날짜 값을 설정
  const parseDate = (date: number | string | undefined): Date | null => {
    if (typeof date === 'number' || typeof date === 'string') {
      const parsedDate = new Date(Number(date));
      return isValid(parsedDate) ? parsedDate : null;
    }
    return null;
  };

  // form context에서 초기값 가져오기
  const initialStartDate = getValues(startNm);
  const initialEndDate = getValues(endNm);

  let startDate = initialStartDate ? parseDate(initialStartDate) : null;
  let endDate = initialEndDate ? parseDate(initialEndDate) : null;

  // emptyInitial이면 날짜 표기 없음
  if (emptyInitial) {
    startDate = endDate = null;
  }
  return (
    <DateRangePicker
      format="yyyy-MM-dd"
      value={[startDate, endDate]}
      onChange={(newValue) => {
        if (typeof newValue === 'object' && newValue !== null) {
          const [startDate, endDate] = newValue;
          if (startDate && endDate) {
            // 시작 날짜는 00:00:00으로, 종료 날짜는 23:59:59로 설정
            const adjustedStartDate = new Date(startDate);
            adjustedStartDate.setHours(0, 0, 0, 0);

            const adjustedEndDate = new Date(endDate);
            adjustedEndDate.setHours(23, 59, 59, 999);

            setValue(startNm, adjustedStartDate.getTime());
            setValue(endNm, adjustedEndDate.getTime());
          }
        }
      }}
      maxDate={new Date(9999, 11, 31)} // 먼 미래의 날짜도 가능하도록 설정
      slots={{ field: SingleInputDateRangeField }} // 1개의 input으로 표시하는 옵션
      slotProps={{
        textField: {
          size: size,
          label: label ?? '기간 선택',
          InputProps: {
            endAdornment: (
              <InputAdornment position="end">
                <CalendarTodayIcon />
              </InputAdornment>
            ),
            disabled: disabled,
          },
        },
      }}
      disabled={disabled}
      {...other}
    />
  );
}
